import { NodeViewContent, NodeViewWrapper } from '@tiptap/react';
import React, { createElement, MouseEvent, useContext, useMemo } from 'react';
import { Button, Caption1, Link as FluentLink, Tooltip } from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import { CheckmarkRegular, Dismiss16Regular, Info16Regular } from '@fluentui/react-icons';

import { ControlledTooltip } from '@shared/components';
import { TMap } from '@shared/types';
import { getVersionId, getLatestVersionId, EItemScreenMode, useItemScreenStore } from '@modules/items';
import { useImpactSourceStore } from '@modules/impacts';

import DocumentContext from '../../../contexts/DocumentContext.context';
import useScrollContent from '../../../hooks/useScrollContent.hook';
import { TDraft, THeading, TInRef, TVersion } from '../../../types';
import { SECTION_AUTO_SELECTED_TOOLTIP } from '@modules/documents/constants';
import { SignInContext } from '../../../../users';
import { buildHref } from '@shared/utils';
import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';

export type TTreeHeading = THeading & {
  children: TTreeHeading[];
};

const Component = React.memo(({ node }: any) => {
  const { doc, version, refTooltip } = useContext(DocumentContext);
  const {
    organization: { mock },
  } = useContext(SignInContext);
  const scrollContent = useScrollContent();

  const latestVersionId = getLatestVersionId(doc); // outside of useMemo
  const tooltip = useMemo(() => {
    if (!version || !latestVersionId) return '';
    const versionId: string | undefined = version ? getVersionId(version) : undefined;

    // console.log(doc.inRefs.filter(ref => ref.headingId === node.attrs.id))
    const inRefs = doc.inRefs.filter(
      (ref) =>
        ref.headingId === node.attrs.id &&
        (ref.sourceDocId === doc._id
          ? version.outRefs.includes(ref.id)
          : (!ref.versionId && versionId === latestVersionId) || ref.versionId === versionId)
    );
    if (!inRefs.length) return '';

    const docRefs: TMap<string, TInRef[]> = {};
    inRefs.forEach((ref) => {
      if (!docRefs[ref.sourceDocId]) docRefs[ref.sourceDocId] = [];
      docRefs[ref.sourceDocId].push(ref);
    });
    const docIds = Object.keys(docRefs);
    const selfIdx = docIds.indexOf(doc._id);
    if (selfIdx >= 0) {
      docIds.splice(selfIdx, 1);
      docIds.unshift(doc._id);
    }

    return (
      <div className="!w-auto">
        Documents referencing this section:
        <ul className="list-disc list-inline mt-1">
          {docIds.map((docId) => (
            <li key={docId} className="mb-0.5 flex flex-wrap">
              <span className="truncate">
                <span className="px-2">•</span>
                {docId === doc._id ? (
                  <span>This document</span>
                ) : (
                  <FluentLink href={buildHref(`/document/${docId}`)} target="_blank">
                    {getReferenceItemLabel(docRefs[docId][0].sourceDocName)}
                  </FluentLink>
                )}
              </span>
              &nbsp;
              <span>
                (
                {docRefs[docId].map((ref, idx) => (
                  <span key={idx}>
                    {idx > 0 && ', '}
                    {docId === doc._id ? (
                      <FluentLink onClick={() => scrollContent({ value: ref.id })}>#{idx + 1}</FluentLink>
                    ) : (
                      <FluentLink target="_blank" href={buildHref(`/document/${docId}`, { elem: ref.id })}>
                        #{idx + 1}
                      </FluentLink>
                    )}
                  </span>
                ))}
                )
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }, [latestVersionId, version, doc.inRefs, node.attrs.id]);

  const { screenMode } = useItemScreenStore();
  const { selectedHeadingIds, autoDisabledIds, toggleHeading } = useImpactSourceStore();

  const isSelected = selectedHeadingIds.has(node.attrs.id);
  const isAutoDisabled = autoDisabledIds.has(node.attrs.id);

  const handleSelect = (e: MouseEvent) => {
    e.stopPropagation();
    toggleHeading(node.attrs.id, version as TVersion | TDraft);
  };

  const impactAnalysisButton = (
    <Button
      size="small"
      disabled={isAutoDisabled}
      appearance={isAutoDisabled ? 'outline' : isSelected ? 'primary' : 'secondary'}
      icon={isAutoDisabled ? <Info16Regular /> : isSelected ? <Dismiss16Regular /> : <CheckmarkRegular />}
      iconPosition={isSelected ? 'after' : 'before'}
      onClick={handleSelect}
    >
      {isSelected ? 'Selected' : 'Analyze'}
    </Button>
  );

  const isH5Ref = node.attrs.level === 5 && mock.isH5RefEnabled;

  // NodeViewWrapper is a div with class 'relative', see addNodeView() in HeadingNode.tsx
  return (
    <NodeViewWrapper>
      <ControlledTooltip content={tooltip} relationship="description" positioning="above-start" mountNode={refTooltip}>
        {!isH5Ref ? (
          createElement(
            `h${node.attrs.level}`,
            { id: node.attrs.id },
            <>
              <span contentEditable={false} className="heading-label">
                {node.attrs.numbering}
              </span>
              <span className="heading-text">
                <NodeViewContent className="content" />
              </span>
            </>
          )
        ) : (
          <span id={node.attrs.id} className="heading-text h5-ref">
            <NodeViewContent className="content" />
          </span>
        )}
      </ControlledTooltip>

      {screenMode === EItemScreenMode.PRE_CHANGE_IMPACT_ANALYSIS && (
        <div className="absolute right-0 top-0 h-full">
          {/* `ControlledTooltip` is unfortunately not working here. Hence we conditionally render the `ImpactAnalysisButton`: */}
          {isAutoDisabled ? (
            <Tooltip content={SECTION_AUTO_SELECTED_TOOLTIP} relationship="description" positioning="after" withArrow>
              {impactAnalysisButton}
            </Tooltip>
          ) : (
            impactAnalysisButton
          )}
        </div>
      )}
    </NodeViewWrapper>
  );
});

export default Component;

/* Prevent dragging of partial heading nodes:
dragstart(view, event) {
  const { schema, doc, selection } = view.state;
  const { from, to} = selection;
  const node = doc.nodeAt(from);

  let hasHeading = false;
  doc.nodesBetween(from, to, (node, pos) => {
    // Push node and position to the nodes array
    // 'pos' is the absolute position of the node, you might need it for complex operations
    if (node.type.name === EMarkSelectorKey.HEADING && (pos < from || pos + node.nodeSize > to))
      hasHeading = true;
  });

  if (hasHeading) {
    event.preventDefault();
    // alert('Block dragging is not allowed');
    return true;
  }
},
*/
